import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { UtilitiesService } from 'src/app/services/utilities/utilities.service';
import { ConfirmActionComponent } from 'src/app/shared/components/confirm-action/confirm-action.component';
import {
  handleFileUploaded,
  handleFileUploadError,
} from 'src/app/shared/components/upload-input/file-upload-helper';
import { ErrorModalComponent } from 'src/app/shared/modals/error-modal/error-modal.component';
import { SuccessModalComponent } from 'src/app/shared/modals/success-modal/success-modal.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-death-registration-application',
  standalone: false,
  templateUrl: './death-registration-application.component.html',
  styleUrl: './death-registration-application.component.scss',
})
export class DeathRegistrationApplicationComponent {
  isSubmitting: boolean;
  isVerifyingAsin: boolean;
  today: string;
  previewed: boolean;
  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private dialog: MatDialog,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private authService: AuthService,
  ) {}

  showErrorMessages: boolean = false;

  form: FormGroup;

  isLoading: boolean;
  searchedDataNotFound: boolean;

  ngOnInit(): void {
    this.today = new Date().toISOString().split('T')[0];

    this.initializeForm();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      surname: ['', [Validators.required]],
      firstName: ['', [Validators.required]],
      middleName: [''],
      gender: ['', [Validators.required]],
      address: ['', [Validators.required]],
      dod: ['', [Validators.required]],
      typeOfDeath: ['', [Validators.required]],
      placeOfDeath: ['', [Validators.required]],
      timeOfDeath: [''],
      proofOfDeath: ['', [Validators.required]],
      deceasedMeansOfId: [''],
      homeTown: ['', [Validators.required]],

      age: ['', [Validators.required, this.rangeValidator(1, 150)]],

      relationAsin: [''],
      relationSurname: ['', [Validators.required]],
      relationFirstName: ['', [Validators.required]],
      relationMiddleName: [''],
      relationPhoneNumber: [
        '',
        [Validators.required, this.phoneNumberValidator() as ValidatorFn],
      ],
      relationAddress: ['', [Validators.required]],
      relationship: ['', [Validators.required]],
      relationMeansOfId: ['', [Validators.required]],
    });
  }

  rangeValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null || value === '') return null;
      if (value < min) {
        return { rangeMin: { requiredMin: min, actual: value } };
      }
      if (value > max) {
        return { rangeMax: { requiredMax: max, actual: value } };
      }
      return null;
    };
  }

  verifyRelationAsin() {
    const asin = this.form.get('relationAsin')?.value;

    this.isVerifyingAsin = true;

    this.apiService
      .get('/utility/asin/validate?tin=' + asin)
      .pipe(
        finalize(() => {
          this.isVerifyingAsin = false;
        }),
      )
      .subscribe(
        (response: any) => {
          this.form.patchValue({
            relationFirstName: response?.firstName,
            relationSurname: response?.lastName,
            relationPhoneNumber: response?.phoneNumber,
            relationAddress: response?.firstName,
          });

          this.openAsinVerificationSuccessModal('ASIN verified successfuly');
        },
        (error) => {
          this.openErrorModal(error?.message);
        },
      );
  }

  openAsinVerificationSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || control.value === undefined) {
        return null;
      }

      const cleanedPhoneNumber = control.value
        .replace(/\s/g, '')
        .replace(/-/g, '');

      const isDigitsOnly = /^\d+$/.test(cleanedPhoneNumber);

      if (
        cleanedPhoneNumber.length === 0 ||
        (cleanedPhoneNumber.length === 11 && isDigitsOnly) ||
        (cleanedPhoneNumber.length === 13 &&
          cleanedPhoneNumber.startsWith('234')) ||
        (cleanedPhoneNumber.length === 14 &&
          cleanedPhoneNumber.startsWith('+234'))
      ) {
        return null;
      } else {
        return { invalidPhoneNumber: true };
      }
    };
  }

  onFileChange(
    fileDetails: { file: File; readableUrl: string } | null,
    controlName: string,
  ) {
    handleFileUploaded(fileDetails, this.form, controlName);
  }

  async mapFormValuesToObject(formValue: any): Promise<any> {
    let proofOfDeathBase64 = '';
    let deceasedMeansOfIdBase64 = '';
    let relationMeansOfIdBase64 = '';

    if (formValue.proofOfDeath?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.proofOfDeath.file,
      );
      proofOfDeathBase64 = fullBase64.split(',')[1];
    }

    if (formValue.deceasedMeansOfId?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.deceasedMeansOfId.file,
      );
      deceasedMeansOfIdBase64 = fullBase64.split(',')[1];
    }

    if (formValue.relationMeansOfId?.file) {
      const fullBase64 = await this.convertFileToBase64(
        formValue.relationMeansOfId.file,
      );
      relationMeansOfIdBase64 = fullBase64.split(',')[1];
    }

    return {
      firstName: formValue.firstName,
      lastName: formValue.surname,
      middleName: formValue.middleName,
      gender: formValue.gender,
      address: formValue.address,
      dod: formValue.dod,
      typeOfDeath: formValue.typeOfDeath,
      placeOfDeath: formValue.placeOfDeath,
      timeOfDeath: formValue.timeOfDeath,
      proofOfDeath: proofOfDeathBase64,
      deceasedMeansOfId: deceasedMeansOfIdBase64,

      age: formValue.age,
      homeTown: formValue.homeTown,

      relationAsin: formValue.relationAsin,
      relationSurname: formValue.relationSurname,
      relationFirstName: formValue.relationFirstName,
      relationMiddleName: formValue.relationMiddleName,
      relationPhoneNumber: formValue.relationPhoneNumber,
      relationAddress: formValue.relationAddress,
      relationship: formValue.relationship,
      relationMeansOfId: relationMeansOfIdBase64,
    };
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  onError(errorResponse: any, controlName: string) {
    handleFileUploadError(errorResponse, this.form, controlName);
  }

  previewForm() {
    this.previewed = true;
    this.form.disable();
  }

  enableEdit() {
    this.previewed = false;
    this.form.enable();
  }

  openSuccessModal(message: string) {
    let dialogRef = this.dialog.open(SuccessModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { successMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.router.navigate(['/admin/death-registration']);
    });
  }

  openErrorModal(message: string) {
    let dialogRef = this.dialog.open(ErrorModalComponent, {
      width: '350px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      data: { errorMessage: message },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  openConfirmModal() {
    let dialogRef = this.dialog.open(ConfirmActionComponent, {
      width: '400px',
      backdropClass: 'modal-form-backdrop',
      hasBackdrop: true,
      panelClass: ['modal-form', 'modal-no-overflow-changed'],
      disableClose: true,

      data: {
        title: 'Submit death registration?',
        body: '',
        confirmText: 'Yes',
        lottieIconPath: 'assets/lottie/alert.json',
        onConfirm: () => {
          this.create();
        },
        onCancel: () => {},
        showCancelButton: true,
        showConfirmButton: true,
      },
    });
  }

  async create(): Promise<void> {
    this.isSubmitting = true;

    try {
      const formData = this.getFormValues();

      const request = await this.mapFormValuesToObject(formData);

      const url = '/death-registration/register';

      this.apiService
        .post(url, request)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          }),
        )
        .subscribe(
          (res) => {
            if (res.success) {
              this.openSuccessModal(
                'Death Registration submitted successfully',
              );
            } else {
              this.openErrorModal(res.error || res.message);
            }
          },
          (error) => {
            this.openErrorModal(error?.message);
          },
        );
    } catch (error) {
      this.isSubmitting = false;
      this.openErrorModal('Failed to process form data');
    }
  }

  getFormValues() {
    const formData = {};
    Object.keys(this.form.controls).forEach((control) => {
      formData[control] = this.form.get(control)?.value;
    });
    return formData;
  }

  back() {
    window.history.back();
  }

  hasAccess(permissions: string[]) {
    return this.authService.hasAnyPermission(permissions);
  }

  startInterswitch(res: any) {
    const url = '/payment/initialize';
    const details = {
      paymentAmount: 5200,
      callbackUrl: environment.envUrl + 'admin/death-registration',
      email: '',
      phoneNumer: this.form.get('relationPhoneNumber')?.value,
      asin: this.form.get('relationAsin')?.value,
      revenueName: 'COURT WEDDING/ CERTIFICATE',
      entityId: res?.data?.id,
      entityType: 'DEATH_REGISTRATION',
    };
    this.apiService.post(url, details).subscribe({
      next: (response) => {
        this.checkout(response.data);
      },
      error: (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      },
    });
  }

  checkout(details: any) {
    var paymentRequest = {
      merchant_code: details?.merchantCode,
      pay_item_id: details?.payItemId,
      txn_ref: details?.orderId,
      cust_id: details?.customerId,
      amount: Math.round((details?.amount * 100 + Number.EPSILON) * 100) / 100,
      currency: '566',
      site_redirect_url: window.location.origin,
      onComplete: (response: any) => this.paymentCallback(response),
      mode: details?.mode,
    };

    if (
      details?.mode === 'LIVE' &&
      details?.splitAccounts &&
      Object.keys(details?.splitAccounts).length > 0
    ) {
      paymentRequest['split_accounts'] = JSON.stringify(details?.splitAccounts);
    }

    (window as any).webpayCheckout(paymentRequest);
  }

  paymentCallback(response: any) {
    const url = '/payment/acknowledge?orderId=' + response?.txnref;
    this.apiService.post(url, {}).subscribe(
      (response) => {
        this.router.navigate(['/admin/death-registration']);
      },
      (error) => {
        console.info(`Unable to reach Interswitch: ${error}`);
      },
    );
    return;
  }
}
